import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Image } from 'antd';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import Carousel, { ICarouselData } from '@components/Carousel';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { BannerData } from '@services/types/General/banners';
import SectionApplications from '@components/PagesMenu/SectionApplications';

import * as S from './styles';

const Home = () => {
  const { t } = useTranslation('main');
  const { keycloak } = useContext(KeycloakContext);
  const { isRoot } = useContext(SessionContext);
  const username = keycloak?.idTokenParsed?.given_name;

  const [previewCarousel, setPreviewCarousel] = useState<{
    visible: boolean;
    imgSrc?: string;
  }>();

  const { data } = useSwr('/service-proxy/banner/validate', null, {
    revalidateOnFocus: true,
    shouldRetryOnError: true,
  });

  const handleOnClickLink = useCallback(
    (link: string) => window.open(link, '_blank'),
    [],
  );

  const handleOnClickImg = useCallback(
    (fileName: string) =>
      setPreviewCarousel({
        visible: true,
        imgSrc: `/banners/${fileName}`,
      }),
    [],
  );

  const handleBanner = useCallback(
    (bannersList: BannerData[]) =>
      bannersList?.map(banner => {
        const { hostname } = window.location;
        const isLink = !!banner?.link && banner?.link !== '';
        const hasExtraDetails = isLink || banner?.fileDetailName;

        const isLocalhost = hostname === 'localhost';

        const imgSrc = `${
          isLocalhost ? process.env.REACT_APP_ROOT_URL : ''
        }/banners/${banner?.fileName}`;

        const defaultProps: ICarouselData = {
          key: banner?.fileName,
          imgSrc,
          buttonText: banner?.buttonText,
          allowButton: !!banner?.link || !!banner?.fileDetailName,
        };

        const handleOnClick = () => {
          if (isLink) {
            handleOnClickLink(banner?.link);
          } else {
            handleOnClickImg(banner?.fileDetailName);
          }
        };

        if (hasExtraDetails) {
          if (banner?.buttonText) {
            defaultProps.buttonOnClick = handleOnClick;
          } else {
            defaultProps.imgOnClick = handleOnClick;
          }
        }

        return defaultProps;
      }),
    [handleOnClickImg, handleOnClickLink],
  );

  const carouselData = useMemo(
    (): {
      banners: ICarouselData[];
      parentBanners: ICarouselData[];
    } => ({
      banners: handleBanner(data?.banners),
      parentBanners: handleBanner(data?.parentBanners),
    }),
    [data?.banners, data?.parentBanners, handleBanner],
  );

  return (
    <S.Container>
      <S.Content>
        <h1 className="home-greetings">
          {t('greetings.hello', { name: username })}
          <br />
          <span>{t('greetings.welcome')}</span>
        </h1>

        <div className="home-content-carousel">
          <Carousel
            data={isRoot ? carouselData?.banners : carouselData?.parentBanners}
          />
        </div>

        <SectionApplications type="home" />

        <div className="home-content-carousel">
          <Carousel
            data={isRoot ? carouselData?.parentBanners : carouselData?.banners}
          />
        </div>
      </S.Content>

      <Image
        src={previewCarousel?.imgSrc}
        height={0}
        preview={{
          visible: previewCarousel?.visible,
          onVisibleChange: visible => setPreviewCarousel({ visible }),
        }}
      />
    </S.Container>
  );
};

export default Home;
